"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio/react";
import { EdgenModal } from "@components/EdgenModal";
import { useGlobalAppModalLimit } from "@hooks/useGlobalAppModalLimit";
import { useIsPc } from "@hooks/useIsPc";
import { requestNativeSurface } from "@features/nativeSurface/requestNativeSurface";
import { Track } from "@/src/lib/tracking";
import {
  dismissGoalActivationSuccessSurface,
  goalActivationSuccessSurfaceStore,
} from "./goalActivationSuccessSurfaceStore";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/GoalConversion/GoalActivationSuccessSurface";

const I18n = I18nUtil.createScoped(Translations);

const GOAL_ACTIVATION_SUCCESS_MODAL_KEY = "goal_activation_success";

export function GoalActivationSuccessSurface() {
  const { request } = useSnapshot(goalActivationSuccessSurfaceStore);
  const [isPc] = useIsPc();
  const [visible, setVisible] = useState(false);
  const closeRef = useRef<() => void>(() => {});
  const { tryOpen } = useGlobalAppModalLimit(
    GOAL_ACTIVATION_SUCCESS_MODAL_KEY,
    { onRemoteClaim: () => closeRef.current() },
  );

  const close = useCallback(() => {
    const current = goalActivationSuccessSurfaceStore.request;
    if (current) {
      Track.event("goal_activation_success_dismiss", {
        goal_id: current.goalId,
      });
      requestNativeSurface({
        key: "push-optin",
        variant: "goal_activation",
      });
    }
    setVisible(false);
    dismissGoalActivationSuccessSurface();
  }, []);
  const goalTitle = request?.goalTitle?.trim();
  closeRef.current = close;

  useEffect(() => {
    if (!request) {
      return;
    }
    tryOpen(() => setVisible(true));
  }, [request, tryOpen]);

  return (
    <EdgenModal
      open={visible}
      onClose={close}
      centered
      title={null}
      footer={null}
      closable={false}
      maskClosable={false}
      contentStyle={{ padding: 0 }}
      styles={
        isPc
          ? {
              // Figma success surface uses a 50% black scrim. An opaque mask
              // makes the whole PC canvas diverge even when the modal itself
              // is geometrically correct.
              mask: {
                backgroundColor: "var(--edgen-background_black_overlay50)",
              },
              wrapper: { transform: "translateY(-85px)" },
              content: {
                padding: 0,
                backgroundColor: "transparent",
                borderRadius: 0,
              },
            }
          : undefined
      }
      width={isPc ? 400 : 300}
      containerClassName="w-[300px] overflow-hidden rounded-3 md:w-[400px]"
    >
      <section
        data-testid="goal-activation-success-surface"
        data-or-view="goal_activate_success_surface"
        data-or-param-goal_id={request?.goalId}
        className="flex flex-col items-center gap-6 bg-background_solid_neutral_secondary p-6 text-center md:p-4"
      >
        <div className="flex w-full flex-col items-center gap-5">
          <div
            data-testid="goal-activation-success-icon"
            data-goal-live-asset="sdk-fallback"
            className="relative h-24 w-[123px]"
          >
            {[
              ["left-2 top-8 rotate-45 bg-content_accent3_primary"],
              ["left-8 top-1 -rotate-12 bg-content_accent1_primary"],
              ["right-7 top-1 -rotate-25 bg-content_accent3_primary"],
              ["right-1 top-8 rotate-3 bg-content_accent3_primary"],
              ["left-8 bottom-1 -rotate-12 bg-content_accent3_primary"],
              ["right-8 bottom-1 rotate-18 bg-content_accent1_primary"],
            ].map(([className], index) => (
              <span
                key={index}
                aria-hidden
                className={`absolute h-2 w-6 rounded-sm ${className}`}
              />
            ))}
            <div className="absolute left-[26px] top-[13px] flex size-[72px] items-center justify-center rounded-full bg-content_primary">
              <span className="relative h-8 w-12" aria-hidden>
                <span className="absolute left-1 top-3 h-2 w-7 rotate-45 rounded-sm bg-white" />
                <span className="absolute left-5 top-1 h-2 w-8 -rotate-45 rounded-sm bg-white" />
              </span>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2">
            <h2 className="headline_regular_s text-content_primary">
              {I18n.t("Your goal is live")}
            </h2>
            <p className="body_m text-content_secondary">
              {goalTitle
                ? I18n.t("From now on I'm pushing this with you — %{var0}.", {
                    var0: goalTitle,
                  })
                : I18n.t("From now on I'm pushing this with you.")}
            </p>
          </div>
        </div>
        <button
          type="button"
          data-openreplay-label="goal_activation_success_got_it"
          data-or-event="goal_activation_success_dismiss"
          data-or-param-goal_id={request?.goalId}
          className="button primary_button h-11 w-full rounded-2"
          onClick={close}
        >
          {I18n.t("Got it")}
        </button>
      </section>
    </EdgenModal>
  );
}
